import React, {useState} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {
  ControlRadioButton,
  CustomButton,
  Layout,
  PageHeader,
  ServicesFormHeader,
} from '../../components';
import {ELEVATOR_SERVICES_FIELDS} from '../../constants/servicesFields';
import {addPdfReport} from '../../actions';
import imageCompression from 'browser-image-compression';

const Title = ({text}) => (
  <Typography fontSize="14px" fontWeight="bold" m="1.5rem 2rem 0.5rem">
    {text.toUpperCase()}
  </Typography>
);

const incrementSerialNumber = () => {
  const currentYear = moment().year();
  let currentSerial = localStorage.getItem('serialNo') || '001';

  let nextSerial = parseInt(currentSerial, 10) + 1;
  if (nextSerial > 999) nextSerial = 1;

  const newSerial = nextSerial.toString().padStart(3, '0');
  const fullSerial = `${newSerial}-${currentYear}`;

  localStorage.setItem('serialNo', newSerial);

  return fullSerial;
};

const AddElevatorReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {elevatorId, elevatorDetails} = location.state || {};
  const {pdfLoading, addPdfLoading} = useSelector(s => s.pdfReports);

  const onBack = () => navigate(-1);

  const [images, setImages] = useState([]);

  const onSubmit = e => {
    e.preventDefault();
    const payload = {date: moment(e.target.date.value).format('DD/MM/YYYY')};
    const serialNumber = incrementSerialNumber();

    if (images.length > 0) {
      payload.images = images; // Store images array in payload
    }
    Object.entries(e.target.elements).map(([key, value]) => {
      if (ELEVATOR_SERVICES_FIELDS.includes(key)) {
        return (payload[key] = value.value);
      }
      return null;
    });
    dispatch(addPdfReport(payload, 'ELEVATOR'));
  };

  const handleImageChange = e => {
    const files = Array.from(e.target.files);
    const imageFiles = files.map(async file => {
      const options = {
        maxSizeMB: 20,
        maxWidthOrHeight: 2026,
        useWebWorker: true,
      };

      try {
        const compressedFile = await imageCompression(file, options);

        const reader = new FileReader();
        return new Promise(resolve => {
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(compressedFile);
        });
      } catch (error) {
        console.error('Image compression error:', error);
        return null;
      }
    });

    Promise.all(imageFiles).then(base64Images => {
      setImages(prevImages => [
        ...prevImages,
        ...base64Images.filter(image => image !== null),
      ]);
    });

    e.target.value = '';
  };

  const handleImageRemove = index => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  return (
    <Layout>
      <PageHeader onBack={onBack} title="Add Elevator Report" />
      <form onSubmit={onSubmit}>
        <ServicesFormHeader elevatorDetails={elevatorDetails} />
        <Title text="Lista e komponenteve të sigurisë" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={1} label="Kyçi i derës së kabinës" />
          <ControlRadioButton index={2} label="Kyçet e dyerve të jashtme" />
          <ControlRadioButton index={3} label="Buferat" />
          <ControlRadioButton index={4} label="Frenat e kabinës" />
          <ControlRadioButton index={5} label="Rregullatori i shpejtësisë" />
          <ControlRadioButton
            index={6}
            label="Valvula e sigurisë"
            hasDefault={false}
          />
        </Box>
        <Title text="Qarku sigurues" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={7} label="Kontakti i dyerve të kabinës" />
          <ControlRadioButton
            index={8}
            label="Kontaktet e dyerve të jashtme të kateve"
          />
          <ControlRadioButton index={9} label="Kontakti i frenave të kabinës" />
          <ControlRadioButton
            index={10}
            label="Kontakti i rregullatorit të shpejtësisë"
          />
          <ControlRadioButton
            index={11}
            label="Kufitaret (i larit, i poshtmi & i pistonit"
          />
          <ControlRadioButton
            index={12}
            label="Stop butonat (në revizion, në PIT)"
          />
        </Box>
        <Title text="Motorri" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton
            index={13}
            label="Pulexho i motorrit/ndihmës/vajosja"
          />
          <ControlRadioButton index={14} label="Frenat/Bobina" />
          <ControlRadioButton index={15} label="Mbajtësi" />
        </Box>
        <Title text="Kabina" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={16} label="Ndalesat në kate" />
          <ControlRadioButton index={17} label="Ndriçimi" />
          <ControlRadioButton index={18} label="Butonat/display" />
        </Box>
        <Title text="Dyert e kabinës" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={19} label="Hapja dhe mbyllja" />
          <ControlRadioButton
            index={20}
            label="Mekanizmi (rollerat, shpata, etj)"
          />
          <ControlRadioButton index={21} label="Panellat e dyerve" />
          <ControlRadioButton index={22} label="Senzorat" />
        </Box>
        <Title text="Dyert e jashtme" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={23} label="Hapja dhe mbyllja" />
          <ControlRadioButton
            index={24}
            label="Mekanizmat (rollerat, panelat)"
          />
          <ControlRadioButton index={25} label="Rrëshqitëset dhe pragjet" />
          <ControlRadioButton index={26} label="Thirrjet dhe display" />
        </Box>
        <Title text="Blloku valvulave" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton
            index={27}
            label="Niveli i vajit në rezervuar"
            hasDefault={false}
          />
          <ControlRadioButton
            index={28}
            label="Rrjedhje të brendshme"
            hasDefault={false}
          />
          <ControlRadioButton
            index={29}
            label="Pompa e dorës"
            hasDefault={false}
          />
          <ControlRadioButton
            index={30}
            label="Valvula e lëshimit manual"
            hasDefault={false}
          />
        </Box>
        <Title text="Gropa e ashensorit" />
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap="10px"
          mx="2rem">
          <ControlRadioButton index={31} label="Lubrifikimi i shinave" />
          <ControlRadioButton index={32} label="Pastrimi i gropës" />
          <ControlRadioButton index={33} label="SISTEMI EMERGJENT" />
        </Box>

        <Title text="Shtoni foto" />
        <Box display="flex" justifyContent="center" mx="2rem">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            style={{marginBottom: '1rem'}}
          />
        </Box>
        {images.length > 0 && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap="10px"
            justifyContent="center"
            my="1rem">
            {images.map((img, idx) => (
              <Box key={idx} position="relative">
                <img
                  src={img}
                  alt={`upload-${idx}`}
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  onClick={() => handleImageRemove(idx)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 1)',
                    },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box display="flex" justifyContent="center" m="2rem">
          <CustomButton
            sx={{width: 'max(300px, 40%)'}}
            type="submit"
            loading={addPdfLoading}>
            Add Report
          </CustomButton>
        </Box>
      </form>
    </Layout>
  );
};

export default AddElevatorReport;
